import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useModalZIndex } from '../../contexts/ModalStackContext';
import {
  fetchCustomerOpenInvoices,
  receiveCustomerPayment,
} from '../../lib/invoices/accountsReceivable';

interface OpenInvoice {
  id: string;
  invoice_number: string | null;
  booking_number: number | null;
  amount_due_cents: number;
  total_cents: number;
  due_at: string | null;
  invoice_date: string | null;
  is_overdue: boolean;
}

interface ReceivePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  customerId: string;
  customerName: string;
  onSuccess?: () => void;
}

const METHODS: { value: 'cash' | 'check' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'other'; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'credit_card', label: 'Credit' },
  { value: 'debit_card', label: 'Debit' },
  { value: 'other', label: 'Other' },
];

const fmtMoney = (cents: number) =>
  '$' + (Math.round(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ReceivePaymentModal({ isOpen, onClose, businessId, customerId, customerName, onSuccess }: ReceivePaymentModalProps) {
  // Same default z-index as before (100000) everywhere; when opened from
  // inside another modal (e.g. Customer Details) the modal-stack context
  // automatically lifts it above the parent.
  const stackZ = useModalZIndex(100000);
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<typeof METHODS[number]['value']>('check');
  const [reference, setReference] = useState('');
  const [allocations, setAllocations] = useState<Record<string, number>>({}); // invoiceId → cents
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !businessId || !customerId) return;
    let active = true;
    setLoading(true);
    setError(null);
    setAmount('');
    setAllocations({});
    fetchCustomerOpenInvoices(businessId, customerId)
      .then((rows: any[]) => { if (active) setInvoices(rows as OpenInvoice[]); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load invoices'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isOpen, businessId, customerId]);

  const totalDueCents = useMemo(() => invoices.reduce((s, i) => s + (i.amount_due_cents || 0), 0), [invoices]);
  const totalAllocatedCents = useMemo(() => Object.values(allocations).reduce((s, v) => s + (v || 0), 0), [allocations]);

  // Auto-allocate oldest-first (invoices arrive oldest-first from the query).
  const autoAllocate = (amountCents: number) => {
    let remaining = amountCents;
    const next: Record<string, number> = {};
    for (const inv of invoices) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, inv.amount_due_cents || 0);
      if (take > 0) next[inv.id] = take;
      remaining -= take;
    }
    setAllocations(next);
  };

  const handleAmountChange = (val: string) => {
    setAmount(val);
    const cents = Math.round((parseFloat(val) || 0) * 100);
    autoAllocate(cents);
  };

  const handleAllocChange = (inv: OpenInvoice, val: string) => {
    const cents = Math.min(Math.max(0, Math.round((parseFloat(val) || 0) * 100)), inv.amount_due_cents || 0);
    setAllocations((prev) => ({ ...prev, [inv.id]: cents }));
  };

  const handleSubmit = async () => {
    setError(null);
    const amountCents = Math.round((parseFloat(amount) || 0) * 100);
    if (amountCents <= 0) { setError('Enter an amount received.'); return; }
    const allocs = Object.entries(allocations)
      .filter(([, c]) => c > 0)
      .map(([invoiceId, amountCents]) => ({ invoiceId, amountCents }));
    setSubmitting(true);
    try {
      await receiveCustomerPayment({
        businessId,
        customerId,
        method,
        reference: reference || null,
        amountReceivedCents: amountCents,
        allocations: allocs,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const unapplied = Math.max(0, Math.round((parseFloat(amount) || 0) * 100) - totalAllocatedCents);

  return createPortal(
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.4)', zIndex: stackZ }}
    >
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[92vh] flex flex-col" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-[17px] font-semibold text-gray-900">Receive Payment</h2>
            <p className="text-xs text-gray-500 mt-0.5">{customerName}</p>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-xl"><X className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium text-gray-600 mb-1">Amount received</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number" min="0" step="0.01" value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-medium text-gray-600 mb-1">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value as any)}
                className="w-full px-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-gray-600 mb-1">Reference (cheque #, etc.)</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional"
              className="w-full px-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white" />
          </div>

          {/* Open invoices */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-semibold text-gray-700">Apply to invoices</span>
              <span className="text-[12px] text-gray-500">Total due: {fmtMoney(totalDueCents)}</span>
            </div>
            {loading ? (
              <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
            ) : invoices.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">No open invoices for this customer.</div>
            ) : (
              <div className="border border-gray-200 rounded-[10px] divide-y divide-gray-100">
                {invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium text-gray-900 truncate">
                        Invoice #{inv.invoice_number || inv.booking_number || '—'}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {inv.due_at ? `Due ${formatDate(inv.due_at)}` : formatDate(inv.invoice_date)} · Balance {fmtMoney(inv.amount_due_cents)}
                        {inv.is_overdue ? ' · overdue' : ''}
                      </div>
                    </div>
                    <div className="relative w-28 flex-shrink-0">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[13px]">$</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={allocations[inv.id] != null ? (allocations[inv.id] / 100).toString() : ''}
                        onChange={(e) => handleAllocChange(inv, e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-6 pr-2 py-1.5 bg-gray-100 border-0 rounded-lg text-[13px] text-right focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="text-[13px] text-red-600">{error}</div>}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 space-y-2">
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-gray-500">Applied to invoices</span>
            <span className="font-semibold text-gray-900">{fmtMoney(totalAllocatedCents)}</span>
          </div>
          {unapplied > 0 && (
            <div className="flex items-center justify-between text-[12px] text-blue-600">
              <span>Saved as customer credit</span>
              <span className="font-medium">{fmtMoney(unapplied)}</span>
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={submitting || Math.round((parseFloat(amount) || 0) * 100) <= 0}
            className="w-full py-3 rounded-[12px] text-white font-semibold text-[15px] disabled:opacity-50"
            style={{ backgroundColor: '#007AFF' }}
          >
            {submitting ? 'Receiving…' : `Receive ${fmtMoney(Math.round((parseFloat(amount) || 0) * 100))}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
